import React, { useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import styles from "./GroupedStackedBarChart.module.scss";

/* ================= TYPES ================= */
interface CategoryData {
  label: string;
  budget: number[];
  bv: number[];
}

interface ChartData {
  years: string[];
  categories: Record<string, CategoryData>;
}


interface Props {
  data: ChartData;
  theme?: Theme;
  showPills?: boolean;
  ALL_PILL_COLOR?: Record<Theme, string>;
  CATEGORY_COLORS?: Record<Theme, string[]>
  THEME_STYLE: any;
  isShowAllPill? : boolean;
}

type Theme = "light" | "dark";

const DEFAULT_CATEGORY_COLORS: Record<Theme, string[]> = {
  light: ["#99B2F5", "#F16A81", "#77CBBF", "#667DBB", "#B2F9FF", "#AF60B1", "#77CBBF", "#F5A066", "#CE4800"],
  dark: ["#5969D6", "#F3596C", "#396E75", "#38539A", "#05A896", "#7E3E80", "#396E75", "#F5A066", "#EF6D46"]
};

const DEFAULT_ALL_PILL_COLOR: Record<Theme, string> = {
  light: "#1677FF", // Ant Design blue (light theme)
  dark: "#69B1FF"   // Softer blue for dark theme
};
/* ================= COMPONENT ================= */
export default function StackedGroupedBarChart({
  data,
  theme = "dark",
  showPills = true,
  CATEGORY_COLORS = DEFAULT_CATEGORY_COLORS,
  THEME_STYLE,
  ALL_PILL_COLOR = DEFAULT_ALL_PILL_COLOR,
  isShowAllPill = true,
}: Props) {
  const categoryKeys = Object.keys(data.categories);
  // Default to first category if "All" pill is not shown
  const [selected, setSelected] = useState<string>(isShowAllPill ? "ALL" : (categoryKeys[0] || "ALL"));

  const colors = CATEGORY_COLORS[theme];
  const currentStyles = THEME_STYLE[theme];
  const effectiveSelection = showPills ? selected : "ALL";

  const option = useMemo(() => {
    const series: any[] = [];
    const isAll = effectiveSelection === "ALL";

    /* ============ STACKED (ALL) ============ */
    if (isAll) {
      categoryKeys.forEach((key, idx) => {
        const cat = data.categories[key];
        const color = colors[idx % colors.length];

        series.push({
          name: cat.label,
          type: "bar",
          stack: "budget",
          barWidth: 26,
          itemStyle: { color, borderRadius: [4, 4, 0, 0] },
          label: {
            show: idx === categoryKeys.length - 1,
            position: "top",
            formatter: "$"
          },
          data: cat.budget
        });

        series.push({
          name: cat.label,
          type: "bar",
          stack: "bv",
          barWidth: 26,
          itemStyle: {
            color: "transparent",
            borderColor: color,
            borderWidth: 2,
            borderRadius: [4, 4, 0, 0]
          },
          label: {
            show: idx === categoryKeys.length - 1,
            position: "top",
            formatter: "BV",
          },
          data: cat.bv
        });
      });
    }

    /* ============ GROUPED (SINGLE) ============ */
    if (!isAll) {
      const idx = categoryKeys.indexOf(effectiveSelection);
      const cat = data.categories[effectiveSelection];
      const color = colors[idx % colors.length];

      series.push({
        name: cat.label,
        type: "bar",
        barWidth: 30,
        itemStyle: { color, borderRadius: [4, 4, 0, 0] },
        label: { show: true, position: "top", formatter: "$" },
        data: cat.budget
      });

      series.push({
        name: cat.label,
        type: "bar",
        barWidth: 30,
        itemStyle: {
          color: "transparent",
          borderColor: color,
          borderWidth: 2,
          borderRadius: [4, 4, 0, 0]
        },
        label: { show: true, position: "top", formatter: "BV" },
        data: cat.bv
      });
    }

    return {
      tooltip: {
        trigger: "item",
        backgroundColor: currentStyles.tooltipBg,
        textStyle: { color: currentStyles.tooltipText },
        formatter: (p: any) => {
          const isBV = p.seriesIndex % 2 === 1;
          return `${p.seriesName}<br/>${isBV ? "BV" : "Budget"} : ${p.value.toLocaleString()}`;
        }
      },

      legend: {
        bottom: 0,
        icon: "circle",
        selectedMode: false,
        textStyle: { color: currentStyles.axis },
        data: isAll
          ? categoryKeys.map(k => data.categories[k].label)
          : [data.categories[effectiveSelection]?.label]
      },
      type: "bar",
      barWidth: 28,
      barGap: "40%",
      barCategoryGap: "20%",

      grid: {
        top: 24,
        left: 40,
        right: 20,
        bottom: 60,
        containLabel: true
      },

      xAxis: {
        type: "category",
        data: data.years,
        axisLabel: { color: currentStyles.axis },
        axisLine: { lineStyle: { color: currentStyles.grid } }
      },

      yAxis: {
        type: "value",
        axisLabel: { color: currentStyles.axis },
        splitLine: { lineStyle: { color: currentStyles.grid } }
      },

      series
    };
  }, [data, effectiveSelection, theme]);

  return (
    <div>
      {/* Pills */}
      {showPills && (
        <div className={styles.pillsOuterContainer}>
          {/* ALL pill */}
          {isShowAllPill && <button
            onClick={() => setSelected("ALL")}
            className={styles.pillsAll}
            style={{
              border: `1px solid ${selected === "ALL" ? ALL_PILL_COLOR[theme] : currentStyles.pillBorder
                }`,
              color: currentStyles.pillText,
            }}
          >
            All
          </button>}

          {/* Category pills */}
          {categoryKeys.map((key, idx) => {
            const isActive = selected === key;
            const color = colors[idx % colors.length];

            return (
              <button
                key={key}
                onClick={() => setSelected(key)}
                className={styles.pillsCategory}
                style={{

                  border: `1px solid ${isActive ? color : currentStyles.pillBorder
                    }`,
                  color: currentStyles.pillText,
                }}
              >
                {/* colored dot */}
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: color
                  }}
                />
                {data.categories[key].label}
              </button>
            );
          })}
        </div>
      )}


      <ReactECharts
        option={option}
        notMerge
        lazyUpdate={false}
        style={{ height: 420 }}
      />
    </div>
  );
}
